import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";

import DetailsModal from "@/components/DetailsModal.vue";
import DialogModal from "@/components/DialogModal.vue";
import ReportTemplateMixin, {
  TemplateKind,
} from "@/mixins/http/ReportTemplateMixin";
import { AdcontrolProfile, ReportTemplate } from "@/models";
import {
  belongsToProfile,
  editBlockedMessage,
  sortByOwnership,
  templateDisplayName,
  templateProfileName,
} from "@/components/Agencies/ReportBuilder/reportTemplate";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";

// Everything the two competitor-template lists - Settings/ReportTemplatesList.vue and
// ReportBuilder/TemplateSelect.vue - do identically: the profile-scoped fetch, the shared
// Vuex cache, ownership and ordering, the details modal and the "edit blocked" dialog.
@Component
export default class ReportTemplateListMixin extends Mixins(
  ReportTemplateMixin
) {
  readonly profile!: AdcontrolProfile;

  @Getter("reportTemplatesCache/cachedTemplates")
  readonly cachedTemplates!: (slug: string) => ReportTemplate[] | null;
  @Mutation("reportTemplatesCache/setCachedTemplates")
  readonly setCachedTemplates!: (payload: {
    slug: string;
    templates: ReportTemplate[];
  }) => void;
  @Mutation("reportTemplatesCache/invalidateTemplatesCache")
  readonly invalidateTemplatesCache!: () => void;

  @Ref("detailsModal") readonly detailsModal!: DetailsModal;
  @Ref("editBlockedModal") readonly editBlockedModal!: DialogModal;

  templates: ReportTemplate[] = [];
  loading = false;
  error = false;
  deleting = false;
  duplicating = false;
  selectedTemplate: ReportTemplate | null = null;

  private fetchPromise: Promise<void> | null = null;

  get hasProfile(): boolean {
    return !!(this.profile && this.profile.slug);
  }

  get sortedTemplates(): ReportTemplate[] {
    return sortByOwnership(this.templates, this.profile);
  }

  belongsToProfile(template: ReportTemplate): boolean {
    return belongsToProfile(template, this.profile);
  }

  displayName(template: ReportTemplate): string {
    return templateDisplayName(template);
  }

  profileName(template: ReportTemplate): string {
    return templateProfileName(template);
  }

  // --- Fetch / cache -------------------------------------------------------

  @Watch("profile.slug", { immediate: true })
  onTemplatesProfileChange() {
    this.onTemplateListReset();
    if (!this.hasProfile) {
      this.templates = [];
      return;
    }
    const cached = this.cachedTemplates(this.profile.slug);
    if (cached) {
      this.templates = cached;
      return;
    }
    this.fetchTemplates();
  }

  // Hook: the settings list clears SettingsList's selection and expansion here. Runs before
  // the refs exist, because the watcher above is `immediate` - override defensively.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTemplateListReset() {}

  // Concurrent callers share one request.
  fetchTemplates(): Promise<void> {
    if (this.fetchPromise) return this.fetchPromise;
    const clear = () => {
      this.fetchPromise = null;
    };
    this.fetchPromise = this.doFetchTemplates().then(clear, clear);
    return this.fetchPromise;
  }

  // Always ends with a request that STARTED after this call. CustomReportExporter
  // invalidates the cache and then awaits refresh(), so joining an in-flight request would
  // hand it back the stale rows it just invalidated.
  refresh(): Promise<void> {
    const inFlight = this.fetchPromise;
    if (!inFlight) return this.fetchTemplates();
    return inFlight.then(() => this.fetchTemplates());
  }

  private async doFetchTemplates(): Promise<void> {
    const slug = this.profile?.slug;
    if (!slug) {
      this.templates = [];
      return;
    }
    this.loading = true;
    this.error = false;
    try {
      const templates = await this.indexReportTemplates(slug, {
        kind: TemplateKind.COMPETITOR,
      });
      // The profile may have changed while the request was in flight: this result belongs
      // to `slug`, not to whatever is selected now, and writing it would poison the cache
      // both surfaces read.
      if (slug !== this.profile?.slug) return;
      this.applyTemplates(templates);
      this.onTemplatesFetched();
    } catch (e) {
      this.onTemplatesFetchError(e);
    } finally {
      this.loading = false;
    }
  }

  // Hook: the wizard picker drops its "in use" selection when the template disappears.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTemplatesFetched() {}

  protected onTemplatesFetchError(e: unknown) {
    ToastInvoker.errorToast(responseErrorMessage(e), e);
    this.error = true;
    this.templates = [];
    this.invalidateTemplatesCache();
  }

  // Keep the cache in step with the list so the two surfaces, which read the same cache,
  // never disagree about which templates exist.
  protected applyTemplates(templates: ReportTemplate[]) {
    this.templates = templates;
    this.setCachedTemplates({ slug: this.profile.slug, templates });
  }

  // --- Delete --------------------------------------------------------------

  // The shared half of every delete flow: dropping the rows and updating the cache, with
  // the failure toast. Confirmation and success feedback stay with the consumer, whose UIs
  // differ. Returns false when the request failed (the error toast is already shown).
  //
  // `bulk` picks the endpoint: a bulk delete of one selected template must not silently
  // become a single DELETE.
  protected async deleteTemplates(
    ids: string[],
    bulk: boolean
  ): Promise<boolean> {
    try {
      await (bulk
        ? this.bulkDestroyReportTemplates(this.profile.slug, ids, true)
        : this.destroyReportTemplate(this.profile.slug, ids[0], true));
      this.applyTemplates(this.templates.filter((t) => !ids.includes(t.id)));
      return true;
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
      return false;
    }
  }

  // --- Duplicate -----------------------------------------------------------

  protected async duplicateTemplate(
    target: ReportTemplate,
    name: string
  ): Promise<boolean> {
    if (this.duplicating) return false;
    this.duplicating = true;
    try {
      await this.duplicateReportTemplate(this.profile.slug, target.id, {
        name,
      });
      await this.refresh();
      return true;
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
      return false;
    } finally {
      this.duplicating = false;
    }
  }

  // --- Details -------------------------------------------------------------

  openDetails(template: ReportTemplate) {
    this.selectedTemplate = template;
    this.$nextTick(() => this.detailsModal.show());
  }

  closeDetails() {
    this.detailsModal.hide();
  }

  // --- Edit gate -----------------------------------------------------------

  // True when the edit was blocked and the user has dismissed the explanation.
  protected async warnIfEditBlocked(
    template: ReportTemplate
  ): Promise<boolean> {
    const message = editBlockedMessage(template);
    if (!message) return false;
    await this.editBlockedModal.dialog(message);
    return true;
  }
}
